import * as React from 'react';

import config from 'js/app/config';
import path from 'js/lib/path';

import type { LinkInfo } from 'bundles/seo/CourseraMetatagsV2Types';
import CourseraMetatags from 'bundles/seo/components/CourseraMetatags';
import type { AuthorProfile } from 'bundles/shared-articles/components/ArticleAuthorProfile';
import GoogleSchemaMarkUp from 'bundles/shared-articles/components/seo/ArticleGoogleSchemaMarkUp';
import FaqGoogleSchemaMarkup from 'bundles/shared-articles/components/seo/ArticlePageFaqGoogleSchemaMarkup';
import type { Breadcrumb } from 'bundles/shared-articles/components/seo/utils/schemaMarkupUtils';
import type { Locale } from 'bundles/shared-articles/types';
import { getLanguageCodeFromCountryCode } from 'bundles/shared-articles/utils';
import type { Faq } from 'bundles/unified-seo-content-common/__generated__/baseContentfulTypes';
import type {
  ArticleFieldsFragment,
  SeoContentContentPageFieldsFragment,
} from 'bundles/unified-seo-content-common/__generated__/contentfulTypes';
import { SEO_CONTENT_PAGE_SLUG, getSEOContentTitle } from 'bundles/unified-seo-content-common/constants';
import type { LocalesDataProps } from 'bundles/unified-seo-content-common/hooks/useArticleLocalesData';
import type { SEOContentPageType } from 'bundles/unified-seo-content-common/types';
import { getCategoryBreadcrumbLink, toBreadcrumbs } from 'bundles/unified-seo-content-common/utils/hubPageUtils';
import { createCanonicalLink, toSubFolderLink } from 'bundles/unified-seo-content-common/utils/seoUtils';

type Props = {
  slug: string;
  pageData: ArticleFieldsFragment | SeoContentContentPageFieldsFragment;
  hubPageLink: string;
  subfolderLocaleCode?: Locale;
  authors?: AuthorProfile[];
  estimatedReadTime?: number;
  pageType: SEOContentPageType;
} & LocalesDataProps;

const ContentPageSEO = ({
  slug,
  pageData,
  hubPageLink,
  locales,
  subfolderLocaleCode,
  defaultRegionCountryCode,
  authors,
  estimatedReadTime,
  pageType,
}: Props) => {
  const { title, intro, body, metaTitle, metaDescription, featuredImage, sys } = pageData;

  if (!title || !slug || !intro || !body || !body?.json) {
    return null;
  }

  const rootDirectory = SEO_CONTENT_PAGE_SLUG[pageType];
  const urlDomain = config.url.domain;
  const countryCode = subfolderLocaleCode?.countryCode;
  const pageUrl = createCanonicalLink(
    slug,
    pageType,
    subfolderLocaleCode
      ? { language: subfolderLocaleCode.languageCode, region: subfolderLocaleCode.countryCode.toLowerCase() }
      : undefined
  );

  const categoryBreadcrumbLinks = getCategoryBreadcrumbLink({ pageData, countryCode, pageType });

  const breadcrumbs: Breadcrumb[] = [
    { name: getSEOContentTitle(pageType), absoluteUrl: path.join(urlDomain, hubPageLink) },
    ...toBreadcrumbs(urlDomain, categoryBreadcrumbLinks),
    { name: title, absoluteUrl: pageUrl },
  ];

  const inlineImages = body?.links?.assets?.block;
  const inlineImagesUrls = inlineImages.length > 0 ? inlineImages.map((image) => image?.url) : [];
  const featuredImageUrl = featuredImage?.url;
  const imageUrls = [featuredImageUrl, ...inlineImagesUrls].filter((url) => !!url);

  const faqs = body?.links?.entries?.block
    .filter((linkEntry) => linkEntry?.__typename === 'Faq')
    .map((faqEntry) => ({
      question: (faqEntry as Faq).question,
      answer: (faqEntry as Faq).answer,
    }));

  const hrefLangOverrides: LinkInfo[] = [
    {
      rel: 'alternate',
      hreflang: 'x-default',
      href: createCanonicalLink(slug, pageType, {
        language: getLanguageCodeFromCountryCode(defaultRegionCountryCode),
        region: defaultRegionCountryCode.toLowerCase(),
      }),
    },
    ...locales.map((locale) => toSubFolderLink(locale, slug, pageType)),
  ];

  const firstPublishedAt = sys?.firstPublishedAt;
  const publishedAt = sys?.publishedAt;

  return (
    <React.Fragment>
      <CourseraMetatags
        metaNameAndProperties={{
          title: metaTitle || title,
          ogAndTwitterTitle: metaTitle || title,
          description: metaDescription || intro,
          absoluteImageHref: featuredImageUrl || undefined,
          disableCourseraDescriptionSuffix: true,
        }}
        hrefLangOverrides={() => hrefLangOverrides}
      />
      <FaqGoogleSchemaMarkup faqs={faqs} />
      <GoogleSchemaMarkUp
        title={title}
        imageUrls={imageUrls}
        createdAt={firstPublishedAt}
        updatedAt={publishedAt}
        estimatedReadTime={estimatedReadTime}
        breadcrumbs={breadcrumbs}
        id={`${pageUrl}#${rootDirectory}`}
        authors={authors}
      />
    </React.Fragment>
  );
};

export default ContentPageSEO;
